/**
 * Centralized help text definitions for all standard daily log fields
 * Used for in-app tooltips and user manual generation
 */

export interface FieldHelpText {
  fieldKey: string;
  label: string;
  description: string;
  howToMeasure?: string;
  whenToLeaveBlank?: string;
  example?: string;
}

export const DAILY_LOG_HELP_TEXT: Record<string, FieldHelpText> = {
  date: {
    fieldKey: "date",
    label: "Date",
    description: "The calendar date when this well operation occurred.",
    howToMeasure: "Select the date from the date picker. Defaults to today's date.",
    whenToLeaveBlank: "Never leave blank. Each log must have a date.",
    example: "12/17/2025",
  },
  time: {
    fieldKey: "time",
    label: "Time",
    description: "The time when the well reading was taken or operation began.",
    howToMeasure: "Enter in 24-hour format (HH:MM) or use the time picker.",
    whenToLeaveBlank: "Can be left blank if exact time is not critical for your operation.",
    example: "08:30 or 14:45",
  },
  operatorInitial: {
    fieldKey: "operatorInitial",
    label: "Operator Initial",
    description: "The initials or identifier of the person who performed this operation.",
    howToMeasure: "Enter your initials or operator code (typically 2-10 characters).",
    whenToLeaveBlank: "Can be left blank, but recommended for accountability and audit trails.",
    example: "JD, ABC, or Operator1",
  },
  meterReadStart: {
    fieldKey: "meterReadStart",
    label: "Meter Read Start",
    description: "The meter reading at the beginning of the pumping period. This value is automatically pre-filled from the previous day's ending reading to ensure continuity.",
    howToMeasure: "Read the value directly from the well meter display. If the auto-filled value doesn't match your meter, enter the correct value—the system will log the discrepancy for audit review.",
    whenToLeaveBlank: "Can be left blank only if your well doesn't have a meter or meter readings are not tracked.",
    example: "1000.50 or 25432.75",
  },
  meterReadEnd: {
    fieldKey: "meterReadEnd",
    label: "Meter Read End",
    description: "The meter reading at the end of the pumping period.",
    howToMeasure: "Read the value directly from the well meter display at the end of operations. Must be greater than or equal to the start reading.",
    whenToLeaveBlank: "Can be left blank only if your well doesn't have a meter or meter readings are not tracked.",
    example: "1500.75 or 25932.50",
  },
  gallonsPumped: {
    fieldKey: "gallonsPumped",
    label: "Gallons Pumped",
    description: "The total volume of water pumped during this period. This is automatically calculated from (Meter Read End - Meter Read Start) × Meter Multiplier.",
    howToMeasure: "This field is calculated automatically by the system. The preview shows the expected value, and the final calculation happens on the server.",
    whenToLeaveBlank: "Automatically calculated if meter readings are provided. Can be blank if meter readings are not available.",
    example: "500.25 (calculated from 1500.75 - 1000.50)",
  },
  gpm: {
    fieldKey: "gpm",
    label: "GPM (Gallons Per Minute)",
    description: "The pumping rate in gallons per minute during this operation.",
    howToMeasure: "Read from the flow meter display, or calculate manually by dividing total gallons by total minutes of operation.",
    whenToLeaveBlank: "Can be left blank if GPM measurement is not available or not required for your operation.",
    example: "250.5 or 180.0",
  },
  hours: {
    fieldKey: "hours",
    label: "Hours",
    description: "The total number of hours the well operated during this period.",
    howToMeasure: "Record the runtime from the hour meter, or calculate from start/end times. Can include decimal hours (e.g., 2.5 for 2 hours 30 minutes).",
    whenToLeaveBlank: "Can be left blank if runtime is not tracked for your operation.",
    example: "8.5 or 12.25",
  },
  remarks: {
    fieldKey: "remarks",
    label: "Remarks",
    description: "Additional notes, observations, or comments about this well operation.",
    howToMeasure: "Enter any relevant information such as maintenance performed, unusual conditions, equipment issues, or operational notes.",
    whenToLeaveBlank: "Can be left blank if there are no special notes for this entry.",
    example: "Pump running smoothly. Replaced filter. Noticed slight pressure drop.",
  },
};

export const CHEMICAL_LOG_HELP_TEXT: Record<string, FieldHelpText> = {
  measuredInchesCl2: {
    fieldKey: "measuredInchesCl2",
    label: "Measured Inches Cl₂",
    description: "The current level of chlorine in the tank, measured in inches from the bottom.",
    howToMeasure: "Use the tank gauge or measuring stick to determine the chlorine level in inches.",
    whenToLeaveBlank: "Can be left blank if chlorine level measurement is not performed for this entry.",
    example: "24.5 or 18.75",
  },
  inchesUsedCl2: {
    fieldKey: "inchesUsedCl2",
    label: "Inches Used Cl₂",
    description: "The amount of chlorine consumed since the last reading, measured in inches.",
    howToMeasure: "Calculate by subtracting current measurement from previous measurement, or read from usage indicator.",
    whenToLeaveBlank: "Can be left blank if usage tracking is not required.",
    example: "2.5 or 1.25",
  },
  inchesNaOClWithDelivery: {
    fieldKey: "inchesNaOClWithDelivery",
    label: "Inches NaOCl (with Delivery)",
    description: "The sodium hypochlorite (bleach) level after delivery, measured in inches.",
    howToMeasure: "Measure the tank level immediately after a chemical delivery to record the new total.",
    whenToLeaveBlank: "Leave blank if no delivery occurred. Only fill when recording a delivery.",
    example: "36.0 or 42.5",
  },
  gallonsDelivered: {
    fieldKey: "gallonsDelivered",
    label: "Gallons Delivered",
    description: "The volume of chemical delivered, calculated automatically from inches used × gallons per inch.",
    howToMeasure: "Automatically calculated by the system based on inches used and the well's chlorine gallons per inch setting.",
    whenToLeaveBlank: "Automatically calculated if inches used is provided. Can be blank if no delivery occurred.",
    example: "125.5 (calculated from 2.5 inches × 50.2 gal/inch)",
  },
  lbsCl2: {
    fieldKey: "lbsCl2",
    label: "Lbs Cl₂",
    description: "The weight of chlorine gas used, measured in pounds.",
    howToMeasure: "Read from the chlorine cylinder scale or weight indicator.",
    whenToLeaveBlank: "Can be left blank if using liquid chlorine instead of gas, or if weight tracking is not required.",
    example: "150.0 or 75.5",
  },
  freeResidualPpm: {
    fieldKey: "freeResidualPpm",
    label: "Free Residual (PPM)",
    description: "The free chlorine residual concentration in the water, measured in parts per million.",
    howToMeasure: "Use a chlorine test kit or analyzer to measure free chlorine. Take sample from the discharge line.",
    whenToLeaveBlank: "Can be left blank if residual testing is not performed for this entry.",
    example: "0.5 or 1.2",
  },
  psi: {
    fieldKey: "psi",
    label: "PSI (Pressure)",
    description: "The system pressure measured in pounds per square inch.",
    howToMeasure: "Read from the pressure gauge on the discharge line or wellhead.",
    whenToLeaveBlank: "Can be left blank if pressure monitoring is not required for your operation.",
    example: "65.0 or 72.5",
  },
  staticValue: {
    fieldKey: "staticValue",
    label: "Static Value",
    description: "The water level in the well when the pump is not running, typically measured in feet below ground level.",
    howToMeasure: "Measure with a water level indicator or sonic probe when pump has been off for at least 30 minutes.",
    whenToLeaveBlank: "Can be left blank if static level measurement is not performed for this entry.",
    example: "45.5 or 62.25",
  },
  pumpingValue: {
    fieldKey: "pumpingValue",
    label: "Pumping Value",
    description: "The water level in the well while the pump is running, typically measured in feet below ground level.",
    howToMeasure: "Measure with a water level indicator or sonic probe while pump is operating at normal capacity.",
    whenToLeaveBlank: "Can be left blank if pumping level measurement is not performed for this entry.",
    example: "52.0 or 68.75",
  },
  topOffCl2: {
    fieldKey: "topOffCl2",
    label: "Top Off Cl₂",
    description: "The amount of chlorine added to bring the tank back to full level.",
    howToMeasure: "Record the amount added when topping off the chlorine tank between deliveries.",
    whenToLeaveBlank: "Leave blank if no top-off occurred. Only fill when adding chlorine outside of regular deliveries.",
    example: "5.5 or 3.25",
  },
  residual: {
    fieldKey: "residual",
    label: "Residual",
    description: "General residual measurement (alternative to free residual PPM).",
    howToMeasure: "Use appropriate test kit for the type of residual being measured.",
    whenToLeaveBlank: "Can be left blank if using the Free Residual PPM field instead, or if not required.",
    example: "0.8 or 1.5",
  },
  chemicalNotes: {
    fieldKey: "chemicalNotes",
    label: "Chemical Notes",
    description: "Additional notes specific to chemical treatment, chlorine levels, or related observations.",
    howToMeasure: "Enter any relevant information about chemical dosing, tank conditions, delivery issues, or test results.",
    whenToLeaveBlank: "Can be left blank if there are no special chemical-related notes for this entry.",
    example: "Chlorine tank refilled. Test kit calibrated. Adjusted feed rate.",
  },
};

/**
 * Get help text for a specific field
 */
export function getFieldHelpText(fieldKey: string, targetTable: "dailyLogs" | "chemicalLogs"): FieldHelpText | null {
  if (targetTable === "dailyLogs") {
    return DAILY_LOG_HELP_TEXT[fieldKey] || null;
  } else if (targetTable === "chemicalLogs") {
    return CHEMICAL_LOG_HELP_TEXT[fieldKey] || null;
  }
  return null;
}

/**
 * Get all help text entries for manual generation
 */
export function getAllHelpText(): { dailyLogs: FieldHelpText[]; chemicalLogs: FieldHelpText[] } {
  return {
    dailyLogs: Object.values(DAILY_LOG_HELP_TEXT),
    chemicalLogs: Object.values(CHEMICAL_LOG_HELP_TEXT),
  };
}
